import { useEffect, useMemo, useState } from "react";
import { Download, Loader2, X, FileArchive, Send } from "lucide-react";
import { toast } from "sonner";
import { studio, apiError, inputCls, btnPrimary, btnGhost, beDate, isAccountant } from "@/lib/studioApi";

const iso = (d) => d.toISOString().slice(0, 10);
const presets = () => {
  const now = new Date();
  const y = now.getFullYear();
  const q = Math.floor(now.getMonth() / 3);
  const qStart = (yy, qq) => new Date(Date.UTC(yy, qq * 3, 1));
  const qEnd = (yy, qq) => new Date(Date.UTC(yy, qq * 3 + 3, 0));
  const prev = q === 0 ? [y - 1, 3] : [y, q - 1];
  return [
    { key: "month", label: "Ce mois", start: iso(new Date(Date.UTC(y, now.getMonth(), 1))), end: iso(new Date(Date.UTC(y, now.getMonth() + 1, 0))) },
    { key: "quarter", label: `T${q + 1} ${y}`, start: iso(qStart(y, q)), end: iso(qEnd(y, q)) },
    { key: "prev_quarter", label: `T${prev[1] + 1} ${prev[0]}`, start: iso(qStart(...prev)), end: iso(qEnd(...prev)) },
    { key: "year", label: `Année ${y}`, start: `${y}-01-01`, end: `${y}-12-31` },
    { key: "prev_year", label: `Année ${y - 1}`, start: `${y - 1}-01-01`, end: `${y - 1}-12-31` },
  ];
};

export const AccountingExport = ({ onClose, onSent }) => {
  const list = useMemo(presets, []);
  const [preset, setPreset] = useState("prev_quarter");
  const [start, setStart] = useState(list[2].start);
  const [end, setEnd] = useState(list[2].end);
  const [scope, setScope] = useState({ ventes: true, achats: true, reglements: true });
  const [ubl, setUbl] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  const [accountant, setAccountant] = useState("");

  useEffect(() => { if (!isAccountant()) studio.get("/profile").then(({ data }) => setAccountant(data.accountant_email || "")).catch(() => {}); }, []);

  useEffect(() => {
    if (!start || !end || end < start) { setPreview(null); return; }
    const t = setTimeout(() => studio.get(`/accounting/export/preview?start=${start}&end=${end}`).then(({ data }) => setPreview(data)).catch(() => setPreview(null)), 250);
    return () => clearTimeout(t);
  }, [start, end]);

  const pick = (p) => { setPreset(p.key); setStart(p.start); setEnd(p.end); };
  const scopes = Object.keys(scope).filter((k) => scope[k]);

  const download = async () => {
    if (!scopes.length && !ubl) { toast.error("Choisissez au moins un journal."); return; }
    setBusy("download");
    try {
      const res = await studio.get(`/accounting/export?start=${start}&end=${end}&scope=${scopes.join(",")}&ubl=${ubl}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `devisto-comptabilite-${start}_${end}.zip` });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export prêt : transmettez le fichier ZIP à votre comptable.");
      onClose?.();
    } catch (e) { toast.error(apiError(e, "Export impossible.")); } finally { setBusy(""); }
  };
  const sendToAccountant = async () => {
    if (!scopes.length && !ubl) { toast.error("Choisissez au moins un journal."); return; }
    setBusy("send");
    try {
      const { data } = await studio.post("/accounting/send-to-accountant", { start, end, scope: scopes.join(","), ubl }, { timeout: 120000 });
      toast.success(`Export ${data.label} envoyé à ${data.sent_to}.`);
      onSent?.(data);
      onClose?.();
    } catch (e) { toast.error(apiError(e, "Envoi impossible.")); } finally { setBusy(""); }
  };

  const Check = ({ k, label, hint, count }) => (
    <label data-testid={`export-scope-${k}`} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${scope[k] ? "border-[#3A5CC8] bg-[#F6F8FD]" : "border-[#E2E7F7] bg-white"}`}>
      <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#3A5CC8]" checked={scope[k]} onChange={(e) => setScope((s) => ({ ...s, [k]: e.target.checked }))} />
      <span className="flex-1 text-sm"><span className="font-semibold text-[#0F2645]">{label}</span>{count !== undefined && <span className="ml-1.5 font-mono text-xs text-[#5C6A87]">({count})</span>}<span className="block text-xs text-[#5C6A87]">{hint}</span></span>
    </label>
  );

  return (
    <div data-testid="export-dialog" className="fixed inset-0 z-[70] flex items-end justify-center bg-[#0F2645]/50 p-0 backdrop-blur-sm sm:items-center sm:p-6" role="dialog">
      <div className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-5 shadow-2xl sm:rounded-3xl sm:p-6" style={{ animation: "devSlideUp .2s ease-out" }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-heading text-lg font-bold text-[#0F2645] inline-flex items-center gap-2"><FileArchive className="h-5 w-5 text-[#3A5CC8]" /> Export comptable</p>
            <p className="mt-1 text-xs text-[#5C6A87]">Un ZIP avec vos journaux en CSV (Excel, WinBooks, Exact, Yuki…) et les factures de vente en UBL.</p>
          </div>
          <button type="button" data-testid="export-close" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[#93A0BC] hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>

        <p className="mt-5 text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Période</p>
        <div className="mt-2 flex flex-wrap gap-2">
          {list.map((p) => (
            <button key={p.key} type="button" data-testid={`export-preset-${p.key}`} onClick={() => pick(p)} className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${preset === p.key ? "border-[#0F2645] bg-[#0F2645] text-white" : "border-[#E2E7F7] bg-white text-[#0F2645] hover:border-[#C9D4F4]"}`}>{p.label}</button>
          ))}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="text-xs font-semibold text-[#5C6A87]">Du<input data-testid="export-start" type="date" lang="fr-BE" className={`${inputCls} mt-1`} value={start} onChange={(e) => { setPreset(""); setStart(e.target.value); }} /></label>
          <label className="text-xs font-semibold text-[#5C6A87]">Au<input data-testid="export-end" type="date" lang="fr-BE" className={`${inputCls} mt-1`} value={end} onChange={(e) => { setPreset(""); setEnd(e.target.value); }} /></label>
        </div>

        <p className="mt-5 text-xs font-bold uppercase tracking-wide text-[#5C6A87]">Journaux</p>
        <div className="mt-2 space-y-2">
          <Check k="ventes" label="Ventes" hint="Factures, acomptes et notes de crédit émis" count={preview?.sales} />
          <Check k="achats" label="Achats" hint="Factures d'achat et avoirs fournisseurs" count={preview?.purchases} />
          <Check k="reglements" label="Règlements" hint="Paiements encaissés (date, moyen, facture)" count={preview?.payments} />
          <label data-testid="export-ubl" className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${ubl ? "border-[#3A5CC8] bg-[#F6F8FD]" : "border-[#E2E7F7] bg-white"}`}>
            <input type="checkbox" className="mt-0.5 h-4 w-4 accent-[#3A5CC8]" checked={ubl} onChange={(e) => setUbl(e.target.checked)} />
            <span className="flex-1 text-sm"><span className="font-semibold text-[#0F2645]">Fichiers UBL (XML)</span><span className="block text-xs text-[#5C6A87]">Un fichier Peppol BIS 3 par facture de vente, importable directement dans le logiciel du comptable</span></span>
          </label>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <p data-testid="export-summary" className="text-xs text-[#5C6A87]">{preview ? `${beDate(preview.start)} → ${beDate(preview.end)}` : "Période invalide"}</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={onClose} className={btnGhost}>Annuler</button>
            {accountant && (
              <button type="button" data-testid="export-send-accountant" disabled={!!busy || !preview} onClick={sendToAccountant} className={btnGhost} title={`Envoyer à ${accountant}`}>
                {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Envoyer à mon comptable
              </button>
            )}
            <button type="button" data-testid="export-download" disabled={!!busy || !preview} onClick={download} className={btnPrimary}>
              {busy === "download" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Télécharger le ZIP
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
